import {WizardStates, WizardUiState} from './WizardStates';
import {
  ApDynformsConfigFieldset
}                                    from '../../../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {BehaviorSubject, Observable} from 'rxjs';
import {
  ApDynGridColumnConfigBase,
  ApDynGridColumnHideConfig
}                                    from '../../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  ApDynGridPagerConfigBase
}                                    from '../../../../ap-dyngrids/config/ap-dyn-grid-pager-config-base';
import {
  ApDynGridPagerWizardBreadcrumbsStepper,
  ApDynGridPagerWizardButton,
  ApDynGridPagerWizardConfig
}                                    from '../../../../ap-dyngrids/config/ap-dyn-grid-pager-wizard-config';
import {
  EventEmitter
}                                    from '@angular/core';
import {
  map
}                                    from 'rxjs/operators';
import {
  ApDynGridStatusColumnConfig
}                                    from '../../../../ap-dyngrids/config/ap-dyn-grid-status-column-config';
import {
  ApDynGridPropertyColumnConfig,
  FilterType
}                                    from '../../../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {
  ApDynGridGroupColumnConfig
}                                    from '../../../../ap-dyngrids/config/ap-dyn-grid-group-column-config';
import {
  TaskManagementDataProviders
}                                    from '../../../task-management-data-providers/task-management-data-providers';
import {
  ITaskManagementPlanBookGridData,
  TaskManagementWizardOpenConfigType
}                                    from '../../../task-management.types';
import ILanguage = Data.Language.ILanguage;
import ITaskManagementPlanSummary = Data.TaskManagement.ITaskManagementPlanSummary;
import TaskManagementModule = Data.TaskManagement.TaskManagementModule;

export class TaskManagementWizardOverviewConfig implements WizardUiState {
  constructor(private dataProviders: TaskManagementDataProviders,
              private dataItem: ITaskManagementPlanSummary,
              private handleStateChange: EventEmitter<WizardStates>,
              private onSelect: BehaviorSubject<boolean>,
              private onExportNotRunning: BehaviorSubject<boolean>) {
  }

  public getFieldsetConfig(_: Array<ITaskManagementPlanBookGridData>): ApDynformsConfigFieldset[] {
    return [];
  }

  public getGridColumnConfig(): Observable<ApDynGridColumnConfigBase[]> {
    return this.dataProviders.languageStore.Listen(s => s.selectedLanguage).pipe(map((l) => {
      const columns = [];
      columns.AddRange(this._getCommonBeginningGridColumnConfig());
      if (this.dataItem.Module === TaskManagementModule.GD_BasicFertilization) {
        columns.AddRange(this._getBaseFertiGridColumnConfig(l));
      } else if (this.dataItem.Module === TaskManagementModule.N_NFertilization) {
        columns.AddRange(this._getNFertiGridColumnConfig(l));
      }
      columns.AddRange(this._getCommonEndingGridColumnConfig(l));
      return columns;
    }));
  }

  public getPagerConfig(breadCrumbsColumns: WizardStates[], _: TaskManagementWizardOpenConfigType): ApDynGridPagerConfigBase {
    return new ApDynGridPagerWizardConfig(
      new ApDynGridPagerWizardButton('Global_Back_With_Arrow', this.handleStateChange, {
        active: this.onExportNotRunning,
        value: WizardStates.Details
      }),
      new ApDynGridPagerWizardBreadcrumbsStepper(breadCrumbsColumns, 2),
      new ApDynGridPagerWizardButton('Task_Mgmt_Export', this.handleStateChange, {
        active: this.onSelect,
        value: WizardStates.Export
      })
    );
  }

  /*
   * Columns which are same for all views at the beginning of the view
   */
  private _getCommonBeginningGridColumnConfig(): ApDynGridColumnConfigBase[] {
    return [
      new ApDynGridStatusColumnConfig({field: 'Status', class: 'StatusClass'}),
      new ApDynGridPropertyColumnConfig({
        title: 'Docu_Ini__Number',
        field: 'Field.FieldNumber',
        filterable: true,
        filterType: FilterType.NUMBER,
        cssClass: 'right',
        headerFilterable: true,
        width: 50
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Docu_Ini_Subnumber',
        field: 'Field.FieldSubnumber',
        filterable: true,
        filterType: FilterType.NUMBER,
        cssClass: 'right',
        headerFilterable: true,
        width: 50
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Global__FieldName',
        field: 'Field.FieldName',
        filterable: true,
        headerFilterable: true,
        sortIndex: 0,
        sortDesc: false,
        width: 160,
        cssClass: 'left',
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Global__Area_Unit',
        field: 'AreaHa',
        cssClass: 'right',
        headerFilterable: true,
        filterable: true,
        width: 90,
        tooltip: (dataItem) => dataItem['AreaOriginal'],
        hide: new ApDynGridColumnHideConfig({
          mapHide: true
        }),
        pipes: [{
          pipe: this.dataProviders.roundNumericPipe,
          args: [this.dataProviders.settingsStore.FirstSetting.DigitsAfterDecimalPoint]
        }]
      })
    ];
  }

  /*
  * Columns which are same for all views at the ending of the view
  */
  private _getCommonEndingGridColumnConfig(language: ILanguage): ApDynGridColumnConfigBase[] {
    return [
      new ApDynGridPropertyColumnConfig({
        title: 'Nutrients__FertilizationDate',
        field: 'ApplDate',
        format: language.DateFormat,
        hide: new ApDynGridColumnHideConfig({
          mapHide: true
        }),
        width: 150,
        tooltip: (dataItem) => this.dataProviders[this.dataItem.Module].getPlanNumberTooltip(dataItem),
        filterable: true,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Task_Mgmt_Export_Format',
        field: 'SourceItem.ExportFormat.Translation',
        width: 150,
        filterable: true,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Task_Mgmt_Export_Target',
        field: 'SelectedExportTargetObject.Translation',
        width: 100,
        filterable: true,
        headerFilterable: true
      })
    ];
  }

  /*
  * Columns for : Grunddüngung (Base fertilization)
  */
  private _getBaseFertiGridColumnConfig(_: ILanguage): ApDynGridColumnConfigBase[] {
    return [
      new ApDynGridGroupColumnConfig({
        title: this.dataProviders[this.dataItem.Module].getProductGridHeader(this.dataItem),
        groupColumns: [
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Product',
            field: 'ProductName',
            hide: new ApDynGridColumnHideConfig({
              mapHide: true
            }),
            width: 120,
            filterable: true,
            headerFilterable: true
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Average_Amount',
            field: 'ApplRate',
            tooltip: (dataItem) => dataItem?.ApplRate,
            filterType: FilterType.NUMBER,
            cssClass: 'right',
            width: 90,
            filterable: true,
            headerFilterable: true,
            pipes: [{
              pipe: this.dataProviders.roundNumericPipe,
              args: [0]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Min',
            field: 'ApplMin',
            filterType: FilterType.NUMBER,
            cssClass: 'right',
            hide: new ApDynGridColumnHideConfig({
              mapHide: true
            }),
            width: 90,
            filterable: true,
            headerFilterable: true,
            pipes: [{
              pipe: this.dataProviders.roundNumericPipe,
              args: [0]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Max',
            field: 'ApplMax',
            filterType: FilterType.NUMBER,
            cssClass: 'right',
            hide: new ApDynGridColumnHideConfig({
              mapHide: true
            }),
            width: 90,
            filterable: true,
            headerFilterable: true,
            pipes: [{
              pipe: this.dataProviders.roundNumericPipe,
              args: [0]
            }]
          }),
        ]
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Nutrients_Pages_Popups__Distribution',
        field: 'ApplicationModeText',
        width: 100,
        hide: new ApDynGridColumnHideConfig({
          mapHide: true
        }),
        filterable: true,
        headerFilterable: true
      })
    ];
  }

  /*
  * Columns for : ND-N Düngung
  */
  private _getNFertiGridColumnConfig(_: ILanguage): ApDynGridColumnConfigBase[] {
    return [
      new ApDynGridPropertyColumnConfig({
        title: 'Global__Product',
        field: 'ProductName',
        hide: new ApDynGridColumnHideConfig({
          mapHide: true
        }),
        width: 120,
        filterable: true,
        headerFilterable: true
      }),
      new ApDynGridGroupColumnConfig({
        title: this.dataProviders[this.dataItem.Module].getProductGridHeader(this.dataItem),
        groupColumns: [
          new ApDynGridPropertyColumnConfig({
            title: 'AgriConnect_View__Reference',
            field: 'ApplRate',
            tooltip: (dataItem) => dataItem?.ApplRate,
            filterType: FilterType.NUMBER,
            cssClass: 'right',
            width: 90,
            filterable: true,
            headerFilterable: true,
            pipes: [{
              pipe: this.dataProviders.roundNumericPipe,
              args: [0]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Min',
            field: 'ApplMin',
            filterType: FilterType.NUMBER,
            cssClass: 'right',
            hide: new ApDynGridColumnHideConfig({
              mapHide: true
            }),
            width: 90,
            filterable: true,
            headerFilterable: true,
            pipes: [{
              pipe: this.dataProviders.roundNumericPipe,
              args: [0]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Max',
            field: 'ApplMax',
            filterType: FilterType.NUMBER,
            cssClass: 'right',
            hide: new ApDynGridColumnHideConfig({
              mapHide: true
            }),
            width: 90,
            filterable: true,
            headerFilterable: true,
            pipes: [{
              pipe: this.dataProviders.roundNumericPipe,
              args: [0]
            }]
          }),
        ]
      })
    ];
  }
}
